import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  HStack,
  Select,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useSocialEntitySync } from "../hooks/useSocialSync";
import { SocialPlatform } from "../types/social.types";
import { ListingEntityType } from "../../components/types/ai.types";

export type SocialPublishPanelProps = {
  entityType: ListingEntityType;
  entityId: string;

  title?: string;
  description?: string;
  price?: number;
  currency?: string;
  location?: string;
  imageUrl?: string;
  images?: string[];
  productUrl?: string;
  category?: string;
  brand?: string;
  aiCaption?: string;
  hashtags?: string[];

  defaultPlatforms?: SocialPlatform[];
};

export const SocialPublishPanel: React.FC<SocialPublishPanelProps> = ({
  entityType,
  entityId,
  title = "",
  description = "",
  price,
  currency = "MWK",
  location,
  imageUrl,
  images,
  productUrl,
  category,
  brand,
  aiCaption,
  hashtags,
  defaultPlatforms = ["facebook", "instagram"],
}) => {
  const toast = useToast();
  const { syncEntity, loading, error } = useSocialEntitySync(
    entityType,
    entityId,
  );
  const [platforms, setPlatforms] =
    useState<SocialPlatform[]>(defaultPlatforms);
  const [mode, setMode] = useState<
    "catalog" | "organic_post" | "reel" | "ad"
  >("organic_post");
  const [useAiContent, setUseAiContent] = useState(true);
  const [requireApproval, setRequireApproval] = useState(true);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const inputBg = useColorModeValue("white", "gray.900");

  const syncablePlatforms = useMemo(
    () => platforms.filter((platform) => platform !== "whatsapp"),
    [platforms],
  );

  const canSync = useMemo(() => {
    return Boolean(
      entityId &&
        syncablePlatforms.length &&
        title.trim() &&
        description.trim() &&
        productUrl?.trim(),
    );
  }, [entityId, syncablePlatforms.length, title, description, productUrl]);

  const handleSync = async () => {
    try {
      if (!title.trim() || !description.trim() || !productUrl?.trim()) {
        throw new Error("Title, description and productUrl are required");
      }

      const rows = await syncEntity({
        entityType,
        entityId,
        platforms: syncablePlatforms,
        mode,
        useAiContent,
        requireApproval,
        listing: {
          entityType,
          entityId,
          title,
          description,
          price,
          currency,
          location,
          imageUrl,
          images,
          productUrl,
          category,
          brand,
          aiCaption,
          hashtags,
        },
      });

      const failed = rows.filter((row) => row.status === "failed");

      toast({
        title: failed.length ? "Sync completed with warnings" : "Sync job created",
        description: failed.length
          ? `${failed.length} platform(s) failed. Check the job list for details.`
          : "PickDengu will publish/sync this listing according to your settings.",
        status: failed.length ? "warning" : "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: err.message || "Sync failed",
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    }
  };

  return (
    <Box border="1px solid" borderColor={borderColor} rounded="2xl" p={5} bg={cardBg}>
      <VStack align="stretch" spacing={4}>
        <Box>
          <Text fontWeight="bold" color={headingColor}>
            Social publish/sync
          </Text>
          <Text fontSize="sm" color={mutedText}>
            Create sync jobs for connected social platforms. WhatsApp campaigns
            use the WhatsApp/campaign module after App Review approval.
          </Text>
        </Box>

        {error && <Text color="red.500">{error}</Text>}

        <CheckboxGroup
          value={platforms}
          onChange={(values) => setPlatforms(values as SocialPlatform[])}
        >
          <Stack direction={{ base: "column", md: "row" }} spacing={4}>
            <Checkbox value="facebook">Facebook</Checkbox>
            <Checkbox value="instagram">Instagram</Checkbox>
            <Checkbox value="tiktok">TikTok</Checkbox>
            <Checkbox value="google">Google Merchant</Checkbox>
            <Checkbox value="whatsapp" isDisabled>
              WhatsApp campaigns
            </Checkbox>
          </Stack>
        </CheckboxGroup>

        <HStack wrap="wrap">
          <Select
            maxW="240px"
            bg={inputBg}
            borderColor={borderColor}
            value={mode}
            onChange={(event) => setMode(event.target.value as any)}
          >
            <option value="organic_post">Organic post</option>
            <option value="catalog">Catalog / product feed</option>
            <option value="reel">Reel / short video</option>
            <option value="ad">Paid advert draft</option>
          </Select>
          <Checkbox
            isChecked={useAiContent}
            onChange={(event) => setUseAiContent(event.target.checked)}
          >
            Use AI content
          </Checkbox>
          <Checkbox
            isChecked={requireApproval}
            onChange={(event) => setRequireApproval(event.target.checked)}
          >
            Require approval
          </Checkbox>
        </HStack>

        <Button
          colorScheme="teal"
          onClick={handleSync}
          isLoading={loading}
          isDisabled={!canSync}
          alignSelf="flex-start"
        >
          Create social sync job
        </Button>
      </VStack>
    </Box>
  );
};
